/**
 * Social Security number (SSN) is a nine-digit number issued to U.S. citizens, permanent residents,
 * and * temporary (working) residents under section 205(c)(2) of the Social Security Act, codified
 * as 42 U.S.C. 405(c)(2).
 *
 * See `http://www.irs.gov/Individuals/General-ITIN-Information` for more information.
 */
import { uniq } from 'lodash';

const BLACKLIST = ['078051120', '219099999', '457555462', '123456789'];
const SSN_WHITELIST = [
  '888888888',
  '666535944',
  '666701451',
  '666390426',
  '666682230',

  // 700credit test accounts
  '666370560',
  '666663246',
];
const SSN_EXPRESSION = /^(?!666|000|9\d{2})\d{3}[- ]{0,1}(?!00)\d{2}[- ]{0,1}(?!0{4})\d{4}$/;
/**
 * Reference: https://www.irs.gov/pub/irs-pdf/p4757.pdf Revised 10/2023
 * 
 * All valid ITINs are a nine-digit number in the same
 * format as the SSN (9XX-8X-XXXX), begins with a “9”
 * and the 4th and 5th digits range from 50 to 65, 70 to 88,
 * 90 to 92, and 94 to 99.
 */
const ITIN_EXPRESSION = /^(9\d{2})([ -]?)(5[0-9]|6[0-5]|7[0-9]|8[0-8]|9[0-2]|9[4-9])([ -]?)(\d{4})$/;

export function isITIN(value: string | null): boolean {
  // Should pass regex validation
  if (!value) return false;

  return ITIN_EXPRESSION.test(value);
}

export default (securityNumber: string | null): boolean => {
  // Should pass regex validation
  if (!securityNumber) return false;

  const onlyNumbers = securityNumber.replace(/\D/g, '');

  if (SSN_WHITELIST.includes(onlyNumbers)) return true;

  if (
    !SSN_EXPRESSION.test(securityNumber) &&
    !ITIN_EXPRESSION.test(securityNumber)
  ) {
    return false;
  }

  // Should not be blacklisted
  if (BLACKLIST.indexOf(onlyNumbers) !== -1) {
    return false;
  }

  // Should not contain only one digit
  if (uniq(onlyNumbers).length < 2) {
    return false;
  }

  // Should accept ITINs

  return true;
};
